"use client";

import { ArrowDownRight, Mail } from "lucide-react";
import type { MouseEvent } from "react";

/**
 * Hero CTA on the /api marketing page. Smooth-scrolls to the
 * `#contact` form without pushing `#contact` into the URL — clean
 * link-and-share behaviour, no leftover hash if the user goes back.
 *
 * Anchor element is preserved (`<a href="#contact">`) so middle-click /
 * cmd-click still opens a usable destination, and the link is
 * keyboard-focusable. The onClick just intercepts the default
 * navigation and runs a smooth scroll instead.
 */
export function ScrollToContactButton({ label }: { label: string }) {
  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    // Honour modifier-clicks (cmd / ctrl / shift / middle) — those open
    // the link the user expects (new tab, etc.) instead of fighting them.
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }
    event.preventDefault();
    const target = document.getElementById("contact");
    if (!target) return;
    // `start` lands the form's top under the sticky nav, which on a
    // tall form leaves the source-list still half-visible above. Using
    // a manual scroll lets us put the form's *heading row* a comfortable
    // ~96px below the top of the viewport on any screen size
    const headerOffset = 96;
    const top =
      target.getBoundingClientRect().top + window.scrollY - headerOffset;
    window.scrollTo({ top, behavior: "smooth" });
  }

  return (
    <a
      href="#contact"
      onClick={handleClick}
      className="inline-flex items-center gap-2 rounded-lg bg-(--accent) px-6 py-3 text-base font-semibold text-(--accent-foreground) transition-colors hover:bg-(--accent)/90"
    >
      <Mail className="h-4 w-4" />
      {label}
      {/* Same-page anchor (smooth-scroll to #contact) → down-right. */}
      <ArrowDownRight className="h-4 w-4" />
    </a>
  );
}
